import type { TouchDto } from '@glory/contracts';
import { t } from '@glory/i18n';
import type { TouchKind } from '@glory/sales/domain';
import {
  Button,
  Callout,
  ContentSwap,
  Dialog,
  DateRangeField,
  EmptyState,
  InfoHint,
  notifyError,
  Select,
  SkeletonRows,
} from '@glory/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { MessageSquareText, Phone, Plus } from 'lucide-react';
import { useState } from 'react';

import { useStaffOptions } from '../../../features/clients/ui/responsible.tsx';
import { failureMessage } from '../../../features/messenger/model/failure.ts';
import {
  touchKindOptions,
  type TouchesSearch,
  parseTouchesSearch,
} from '../../../features/sales/model/touches.ts';
import { useTouchActions } from '../../../features/sales/runtime/touch-actions.ts';
import { useSalesConfig } from '../../../features/sales/runtime/use-config.ts';
import { SalesHeader } from '../../../features/sales/ui/sales-tabs.tsx';
import { TouchDialog } from '../../../features/sales/ui/touch-dialog.tsx';
import { TouchList } from '../../../features/sales/ui/touch-list.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { requirePermissionRoute } from '../../../lib/permissions.ts';

export const Route = createFileRoute('/_app/sales/touches')({
  validateSearch: (search: Record<string, unknown>) => parseTouchesSearch(search),
  beforeLoad: requirePermissionRoute('sales.use'),
  component: TouchesPage,
});

interface DialogState {
  readonly mode: 'call' | 'touch';
  readonly edit: TouchDto | null;
}

/**
 * «Касания» — журнал звонков, встреч, писем и сообщений со сводками «кто, кому, о чём договорились».
 * Фильтры «кто, вид, период» живут в адресе; сотрудник без `sales.read_all` видит только касания по своим
 * клиентам и сделкам. «Записать звонок» (и команда ⌘K) — клиент или контакт, сводка и сразу следующий шаг.
 */
function TouchesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { timeZone } = useSalesConfig();
  const staff = useStaffOptions();
  const actions = useTouchActions();
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const list = useQuery({
    ...orpc.sales.touches.list.queryOptions({
      input: {
        ...(search.who === undefined ? {} : { authorId: search.who }),
        ...(search.kind === undefined ? {} : { kind: search.kind }),
        ...(search.from === undefined ? {} : { from: search.from }),
        ...(search.to === undefined ? {} : { to: search.to }),
      },
    }),
    placeholderData: keepPreviousData,
  });
  const items = list.data?.items ?? [];
  const filtered = search.who !== undefined || search.kind !== undefined || search.from !== undefined;

  // «Записать звонок» из ⌘K приходит отметкой в адресе: окно открыто, пока она есть, и закрывается вместе с ней.
  const shown: DialogState | null = dialog ?? (search.call === true ? { mode: 'call', edit: null } : null);
  const closeDialog = () => {
    setDialog(null);
    if (search.call === true)
      void navigate({ search: (prev) => ({ ...prev, call: undefined }), replace: true });
  };

  const setFilter = (patch: Partial<TouchesSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };
  const remove = (touch: TouchDto) => {
    actions.remove(touch).catch((error: unknown) => {
      notifyError(failureMessage(error, t('sales.saveFailed')));
    });
  };

  return (
    <div className="relative flex min-h-full w-full flex-col gap-4 px-4 py-5 md:px-6">
      <SalesHeader
        actions={
          <>
            <Button
              variant="primary"
              onClick={() => {
                setDialog({ mode: 'call', edit: null });
              }}
            >
              <Phone /> {t('sales.touchLog.recordCall')}
            </Button>
            <Button
              onClick={() => {
                setDialog({ mode: 'touch', edit: null });
              }}
            >
              <Plus /> {t('sales.touchLog.record')}
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Select<string>
          size="sm"
          aria-label={t('sales.touchLog.filterWho')}
          placeholder={t('sales.touchLog.filterWho')}
          className="w-full sm:w-48"
          value={search.who ?? null}
          options={staff.map((person) => ({ value: person.id, label: person.name }))}
          clearable
          onValueChange={(who) => {
            setFilter({ who: who ?? undefined });
          }}
        />
        <Select<TouchKind>
          size="sm"
          aria-label={t('sales.touchLog.filterKind')}
          placeholder={t('sales.touchLog.filterKind')}
          className="w-full sm:w-44"
          value={search.kind ?? null}
          options={touchKindOptions()}
          clearable
          onValueChange={(kind) => {
            setFilter({ kind: kind ?? undefined });
          }}
        />
        <DateRangeField
          size="sm"
          label={t('sales.touchLog.filterPeriod')}
          hideLabel
          className="w-full sm:w-64"
          value={
            search.from !== undefined && search.to !== undefined ? { from: search.from, to: search.to } : null
          }
          onValueChange={(range) => {
            setFilter({ from: range?.from, to: range?.to });
          }}
        />
        <InfoHint
          label={t('sales.tabTouches')}
          hint={{
            title: t('sales.tabTouches'),
            text: t('sales.hints.touches.text'),
            example: t('sales.hints.touches.example'),
          }}
        />
        {filtered ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setFilter({ who: undefined, kind: undefined, from: undefined, to: undefined });
            }}
          >
            {t('sales.resetFilters')}
          </Button>
        ) : null}
      </div>

      <ContentSwap
        swapKey={list.isError ? 'error' : list.isPending ? 'loading' : items.length === 0 ? 'empty' : 'list'}
      >
        {list.isError ? (
          <Callout className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1">{list.error.message}</span>
            <Button
              size="sm"
              onClick={() => {
                void list.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          </Callout>
        ) : list.isPending ? (
          <SkeletonRows rows={5} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MessageSquareText />}
            title={filtered ? t('sales.touchLog.emptyFilteredTitle') : t('sales.touchLog.emptyTitle')}
            description={filtered ? t('sales.touchLog.emptyFilteredText') : t('sales.touchLog.emptyText')}
            action={
              filtered ? (
                <Button
                  onClick={() => {
                    setFilter({ who: undefined, kind: undefined, from: undefined, to: undefined });
                  }}
                >
                  {t('sales.resetFilters')}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => {
                    setDialog({ mode: 'call', edit: null });
                  }}
                >
                  <Phone /> {t('sales.touchLog.recordCall')}
                </Button>
              )
            }
          />
        ) : (
          <>
            <TouchList
              items={items}
              timeZone={timeZone}
              onEdit={(touch) => {
                setDialog({ mode: 'touch', edit: touch });
              }}
              onRemove={remove}
            />
            {list.data.total > items.length ? (
              <p className="text-small text-muted">
                {t('sales.moreHint', { count: items.length, total: list.data.total })}
              </p>
            ) : null}
          </>
        )}
      </ContentSwap>

      <Dialog
        open={shown !== null}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        {shown === null ? null : <TouchDialog mode={shown.mode} edit={shown.edit} onDone={closeDialog} />}
      </Dialog>
    </div>
  );
}
